/**
 * LiveCoach.tsx — the GUI v2 live call copilot (sidebar + full-screen).
 *
 * Replaces the v1 panel (static 4-item checklist + nudge box) with the
 * context-aware experience: pinned call card, six-zone stage strip, smart
 * checklist with evidence, primary/secondary nudges (WHY / DONE / DISMISS),
 * deterministic vehicle pricing, reason-ranked Value Points, objection helper,
 * close support, and the four lifecycle states (idle / incoming / live /
 * wrap-up). Both layouts are the SAME components at different densities
 * (req 9.3) — ≥1100px renders the three-zone full-screen, narrower renders
 * the 650px-style priority stack.
 *
 * TRANSPORT (unchanged from v1): WebSocket to `${VITE_VOICE_WS_URL}/coach?token=…`
 * with capped-backoff reconnect; ?demo=1 (or no URL) replays the scripted demo
 * call. NEW: one-tap corrections go UPSTREAM on the same socket as
 * PanelMessages — applied optimistically here, reconciled by engine snapshots.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { getAccessToken } from "@/lib/supabase";
import { api } from "@/lib/api";
import { Badge, PageHeader } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import type { CoachingEvent, CriterionKey, LiveLine, PanelMessage } from "@/types";
import { STAGE_ZONE } from "@/types";
import { initialState, reducer, type LiveV2State, type PanelNudge } from "@/live/state";
import { DEMO_SEQUENCE } from "@/live/demo";
import { CallCard, type CallCardActions } from "@/live/CallCard";
import { Checklist, Guidance, StageStrip, TranscriptPanel } from "@/live/panels";
import { PricingPanel } from "@/live/PricingPanel";
import { ClosePanel, ObjectionPanel, ValuePointsPanel } from "@/live/salesPanels";
import { WrapUp } from "@/live/WrapUp";
import { PREPAY_THRESHOLD_CENTS, recommendTier, tier } from "@/live/content/pricing";
import { VALUE_POINTS, rankValuePoints } from "@/live/content/valuePoints";

type ConnStatus = "demo" | "connecting" | "open" | "closed" | "no-session";

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];

/* ── Layout override (req 9.x) ─────────────────────────────────────────────── */
type LayoutMode = "auto" | "sidebar" | "full";
const LAYOUT_PREF_KEY = "tkcc.live.layout";

function readLayoutPref(): LayoutMode | null {
  try {
    const v = window.localStorage.getItem(LAYOUT_PREF_KEY);
    return v === "auto" || v === "sidebar" || v === "full" ? v : null;
  } catch {
    return null;
  }
}
function writeLayoutPref(mode: LayoutMode): void {
  try {
    window.localStorage.setItem(LAYOUT_PREF_KEY, mode);
  } catch {
    /* private mode / storage disabled — the in-memory choice still applies */
  }
}


export function LiveCoach() {
  const { session, initializing } = useAuth();
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const [conn, setConn] = useState<ConnStatus>("connecting");
  const wsRef = useRef<WebSocket | null>(null);

  const wsUrlBase = import.meta.env.VITE_VOICE_WS_URL as string | undefined;
  const demo = useMemo(() => {
    const forced = new URLSearchParams(window.location.search).get("demo") === "1";
    return forced || !wsUrlBase;
  }, [wsUrlBase]);

  // ── upstream panel messages (corrections stick — req A.6 / experience #5) ──
  const send = useCallback((msg: PanelMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ws.OPEN) {
      try {
        ws.send(JSON.stringify(msg));
      } catch {
        /* a dropped correction frame is recoverable — local state already applied */
      }
    }
  }, []);

  // Which coached lines are live RIGHT NOW (from lines_status). Kept OUTSIDE the
  // reducer so it survives a line-switch reconnect (the reducer resets per call).
  const [linesLive, setLinesLive] = useState<string[]>([]);
  const handleEvent = useCallback((event: CoachingEvent) => {
    if (event.type === "lines_status") {
      setLinesLive(event.live);
      return;
    }
    dispatch({ kind: "event", event });
  }, []);

  // ── line auto-follow + chips ──────────────────────────────────────────────
  // Default is AUTO: follow whichever line has a live call; if the watched line
  // goes idle and another is live, jump to it. The user can PIN a line via its
  // chip (e.g. when both lines are live). Switching reconnects with ?watch=<ext>
  // and the hub hydrates that line's current state (server holds it).
  const [pinnedExt, setPinnedExt] = useState<string | null>(null); // null = AUTO
  const [autoWatch, setAutoWatch] = useState<string | null>(null);
  useEffect(() => {
    setAutoWatch((prev) => {
      if (linesLive.length === 0) return null; // nothing live → own line / waiting
      if (prev && linesLive.includes(prev)) return prev; // stay put while still live
      return linesLive[0] ?? null;
    });
  }, [linesLive]);
  const effectiveWatch = pinnedExt ?? autoWatch;

  // The watchable lines (coached extensions + rep names) + the transcript-visible
  // admin toggle. Skipped in demo / signed-out; any authed user may fetch + watch.
  const linesEnabled = !demo && !initializing && !!session;
  const linesQuery = useQuery({
    queryKey: ["live", "lines"],
    queryFn: ({ signal }) => api.liveLines(signal),
    enabled: linesEnabled,
    staleTime: 5 * 60_000,
  });
  const lines: LiveLine[] = linesQuery.data?.lines ?? [];
  // Demo replays transcript lines, so show the panel there; live reads the admin toggle.
  const showTranscript = demo ? true : (linesQuery.data?.show_transcript ?? false);

  // ── demo transport ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!demo) return;
    setConn("demo");
    dispatch({ kind: "reset" });
    const timers = DEMO_SEQUENCE.map((step) => window.setTimeout(() => handleEvent(step.event), step.at));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [demo, handleEvent]);

  // ── live transport (capped-backoff reconnect, fresh token per attempt) ────
  const hasSession = !!session;
  useEffect(() => {
    if (demo || initializing || !wsUrlBase) return;
    if (!hasSession) {
      setConn("no-session");
      return;
    }
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let retryTimer: number | undefined;
    dispatch({ kind: "reset" });

    const connect = async () => {
      if (stopped) return;
      setConn("connecting");
      const token = await getAccessToken();
      if (stopped) return;
      if (!token) {
        setConn("closed");
        scheduleRetry();
        return;
      }
      const watchParam = effectiveWatch ? `&watch=${encodeURIComponent(effectiveWatch)}` : "";
      const url = `${wsUrlBase.replace(/\/$/, "")}/coach?token=${encodeURIComponent(token)}${watchParam}`;
      try {
        ws = new WebSocket(url);
      } catch {
        setConn("closed");
        scheduleRetry();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        attempt = 0;
        setConn("open");
      };
      ws.onerror = () => ws?.close();
      ws.onclose = () => {
        if (stopped) return;
        setConn("closed");
        scheduleRetry();
      };
      ws.onmessage = (msg) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(typeof msg.data === "string" ? msg.data : "");
        } catch {
          return;
        }
        if (parsed && typeof parsed === "object" && "type" in parsed) handleEvent(parsed as CoachingEvent);
      };
    };
    const scheduleRetry = () => {
      if (stopped) return;
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 15000;
      attempt += 1;
      retryTimer = window.setTimeout(() => void connect(), delay);
    };
    void connect();
    return () => {
      stopped = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
      wsRef.current = null;
    };
  }, [demo, initializing, hasSession, wsUrlBase, handleEvent, effectiveWatch]);

  // ── nudge expiry sweep (req 3.2: stale nudges auto-expire) ─────────────────
  const timersRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const timers = timersRef.current;
    for (const nudge of state.nudges) {
      if (timers.has(nudge.id)) continue;
      const handle = window.setTimeout(() => {
        timers.delete(nudge.id);
        dispatch({ kind: "expireNudge", id: nudge.id });
      }, Math.max(1000, nudge.ttlMs));
      timers.set(nudge.id, handle);
    }
    for (const [id, handle] of timers) {
      if (!state.nudges.some((n) => n.id === id)) {
        window.clearTimeout(handle);
        timers.delete(id);
      }
    }
  }, [state.nudges]);
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const handle of timers.values()) window.clearTimeout(handle);
      timers.clear();
    };
  }, []);

  // ── layout (req 9.x): full-screen 3-zone vs 650px sidebar stack ───────────
  // Default is responsive (≥1100px → full-screen), but a manual override lets
  // you force either layout regardless of window width — handy for review and
  // for the side-panel shell. Precedence: ?layout=sidebar|full (one-off) →
  // saved preference (localStorage) → "auto" (responsive).
  const [responsiveWide, setResponsiveWide] = useState(() =>
    window.matchMedia("(min-width: 1100px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1100px)");
    const onChange = (e: MediaQueryListEvent) => setResponsiveWide(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("layout");
    if (fromUrl === "sidebar" || fromUrl === "full") return fromUrl;
    const saved = readLayoutPref();
    return saved ?? "auto";
  });
  const chooseLayout = useCallback((mode: LayoutMode) => {
    setLayoutMode(mode);
    writeLayoutPref(mode);
  }, []);
  const wide = layoutMode === "auto" ? responsiveWide : layoutMode === "full";

  // ── action wiring (optimistic local + upstream) ────────────────────────────
  const cardActions: CallCardActions = useMemo(
    () => ({
      setName(field, value) {
        dispatch({ kind: "setCardField", field, value });
        send({ type: "card_set", patch: { [field]: value || null } });
      },
      setVehicle(index, patch) {
        dispatch({ kind: "setVehicle", index, patch });
        send({ type: "card_set", patch: { vehicle: { index, ...patch } } });
      },
      setServiceInterest(value) {
        dispatch({ kind: "setServiceInterest", value });
        send({ type: "card_set", patch: { serviceInterest: value } });
      },
      setActiveVehicle(index) {
        dispatch({ kind: "setActiveVehicle", index });
        send({ type: "card_set", patch: { activeVehicle: index } });
      },
      reason(key, reasonState) {
        dispatch({ kind: "confirmReason", key, state: reasonState });
        send({ type: "reason_set", key, state: reasonState });
      },
    }),
    [send],
  );

  const nudgeActions = useMemo(
    () => ({
      done(n: PanelNudge) {
        dispatch({ kind: "doneNudge", id: n.id });
        send({ type: "nudge_done", id: n.id, criterionKey: n.criterionKey });
        if (n.criterionKey) send({ type: "checklist_set", key: n.criterionKey, covered: true });
      },
      dismiss(n: PanelNudge) {
        dispatch({ kind: "dismissNudge", id: n.id });
        send({ type: "nudge_dismiss", id: n.id, promptId: n.promptId ?? null });
      },
    }),
    [send],
  );

  const toggleChecklist = useCallback(
    (key: CriterionKey) => {
      const item = state.checklist.find((c) => c.key === key);
      dispatch({ kind: "toggleChecklist", key });
      send({ type: "checklist_set", key, covered: !(item?.covered ?? false) });
    },
    [send, state.checklist],
  );

  const setDisposition = useCallback(
    (key: string) => {
      dispatch({ kind: "setDisposition", key });
      send({ type: "disposition_set", key });
    },
    [send],
  );

  const copyText = useCallback((text: string) => {
    void navigator.clipboard?.writeText(text);
  }, []);

  if (initializing) return <LoadingState label="Loading…" />;

  const zone = STAGE_ZONE[state.stageId];
  const showClose = state.phase === "live" && (zone === "close_book" || state.buyingSignal);
  const vehicle = state.card.vehicles[state.card.activeVehicle] ?? state.card.vehicles[0];
  const overThousand = (() => {
    if (!vehicle?.pricingClass) return false;
    const rec = recommendTier(state.card.reasons.map((r) => r.key));
    return tier(rec.anchor).fullPackageCents[vehicle.pricingClass] >= PREPAY_THRESHOLD_CENTS;
  })();

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Live Coach"
        subtitle={subtitleFor(conn, state)}
        actions={
          <div className="flex items-center gap-2">
            {!demo && lines.length > 0 && (
              <LineChips lines={lines} live={linesLive} pinned={pinnedExt} active={effectiveWatch} onPick={setPinnedExt} />
            )}
            <LayoutToggle mode={layoutMode} wide={wide} onChange={chooseLayout} />
            <ConnBadge conn={conn} phase={state.phase} />
          </div>
        }
      />
      <div className="flex-1 overflow-y-auto p-3">
        {state.phase === "idle" && <IdleView conn={conn} />}
        {state.phase === "incoming" && <IncomingView counterpart={state.counterpart} />}
        {state.phase === "wrapup" && (
          <div className="mx-auto max-w-xl space-y-3">
            <CallCard card={state.card} actions={cardActions} />
            <WrapUp state={state} onDisposition={setDisposition} />
          </div>
        )}
        {state.phase === "live" && (
          wide ? (
            /* ── Full-screen: three zones (req 9.2) ── */
            <div className="grid grid-cols-[1fr_1.3fr_1.1fr] gap-4">
              <div className="space-y-3">
                <StageStrip stageId={state.stageId} />
                <Checklist checklist={state.checklist} onToggle={toggleChecklist} expanded />
                {showTranscript && <TranscriptPanel lines={state.transcript} />}
              </div>
              <div className="space-y-3">
                <CallCard card={state.card} actions={cardActions} />
                <Guidance
                  nudges={state.nudges}
                  buyingSignal={state.buyingSignal}
                  buyingSignalSeen={showClose}
                  compliance={state.compliance}
                  onDismissCompliance={() => dispatch({ kind: "dismissCompliance" })}
                  actions={nudgeActions}
                />
                {state.lastCustomerLine && (
                  <p className="truncate rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
                    Customer: “{state.lastCustomerLine}”
                  </p>
                )}
                <ObjectionPanel activeId={state.activeObjectionId} onOpen={(id) => dispatch({ kind: "openObjection", id })} />
                {showClose && <ClosePanel card={state.card} overThousand={overThousand} onDone={() => undefined} />}
              </div>
              <div className="space-y-3">
                <PricingPanel card={state.card} expanded onCopySummary={copyText} />
                <ValuePointsPanel card={state.card} pinned={state.pinnedValuePoints} onPin={(id) => dispatch({ kind: "togglePin", id })} expanded />
              </div>
            </div>
          ) : (
            /* ── Sidebar: single priority stack (req 9.1 / A.4) ── */
            <div className="mx-auto max-w-[650px] space-y-3">
              <CallCard card={state.card} actions={cardActions} />
              <StageStrip stageId={state.stageId} />
              <Guidance
                nudges={state.nudges}
                buyingSignal={state.buyingSignal}
                buyingSignalSeen={showClose}
                compliance={state.compliance}
                onDismissCompliance={() => dispatch({ kind: "dismissCompliance" })}
                actions={nudgeActions}
              />
              <Checklist checklist={state.checklist} onToggle={toggleChecklist} />
              {showTranscript && <TranscriptPanel lines={state.transcript} />}
              <PricingPanel card={state.card} expanded={false} onCopySummary={copyText} />
              {showClose && <ClosePanel card={state.card} overThousand={overThousand} onDone={() => undefined} />}
              <ObjectionPanel activeId={state.activeObjectionId} onOpen={(id) => dispatch({ kind: "openObjection", id })} />
              <ValuePointsPanel card={state.card} pinned={state.pinnedValuePoints} onPin={(id) => dispatch({ kind: "togglePin", id })} expanded={false} />
            </div>
          )
        )}
      </div>
    </div>
  );
}

function subtitleFor(conn: ConnStatus, state: LiveV2State): string {
  if (conn === "no-session") return "Sign in to start live coaching.";
  if (conn === "closed") return "Disconnected — reconnecting…";
  if (conn === "connecting") return "Connecting to the live coach…";
  if (state.phase === "live") return state.counterpart ? `On a call with ${state.counterpart}` : "On a live call";
  if (state.phase === "wrapup") return "Call ended — tag the outcome and copy the details.";
  return conn === "demo" ? "Demo mode — replaying a sample call." : "Ready — listening for the next call.";
}

/** Segmented Auto / Sidebar / Full control. "Auto" follows window width; the
 *  others force a layout. The active dot on Auto shows which it resolved to. */
function LayoutToggle({
  mode,
  wide,
  onChange,
}: {
  mode: LayoutMode;
  wide: boolean;
  onChange: (m: LayoutMode) => void;
}) {
  const opts: Array<{ id: LayoutMode; label: string }> = [
    { id: "auto", label: wide ? "Auto · full" : "Auto · side" },
    { id: "sidebar", label: "Sidebar" },
    { id: "full", label: "Full" },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded-md border text-xs" role="group" aria-label="Layout">
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={mode === o.id}
          className={
            "border-l px-2 py-1 first:border-l-0 transition " +
            (mode === o.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Line chips: "Auto" (follow the live line) + one per coached line. A chip is
 *  GREEN when that line has a live call; the watched line gets a ring. Clicking
 *  a line pins it (overrides auto); "Auto" returns to following the live line.
 *  When both lines are live, click a chip to put that line full-screen. */
function LineChips({
  lines,
  live,
  pinned,
  active,
  onPick,
}: {
  lines: LiveLine[];
  live: string[];
  pinned: string | null; // null = AUTO
  active: string | null; // the line actually being watched
  onPick: (ext: string | null) => void;
}) {
  const base = "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition";
  return (
    <div className="flex items-center gap-1" role="group" aria-label="Line">
      <button
        type="button"
        onClick={() => onPick(null)}
        aria-pressed={pinned === null}
        className={`${base} ${pinned === null ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
      >
        Auto
      </button>
      {lines.map((l) => {
        const isLive = live.includes(l.extension);
        const isActive = active === l.extension;
        const name = l.rep_name ? l.rep_name.split(" ")[0] : "Ext";
        return (
          <button
            key={l.extension}
            type="button"
            onClick={() => onPick(l.extension)}
            aria-pressed={pinned === l.extension}
            title={isLive ? "Live call on this line" : "No active call"}
            className={[
              base,
              isLive
                ? "border-emerald-400 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                : "text-muted-foreground hover:bg-accent",
              isActive ? "ring-2 ring-ring" : "",
            ].join(" ")}
          >
            <span
              aria-hidden
              className={`h-1.5 w-1.5 rounded-full ${isLive ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/40"}`}
            />
            {name} · {l.extension}
          </button>
        );
      })}
    </div>
  );
}

function ConnBadge({ conn, phase }: { conn: ConnStatus; phase: LiveV2State["phase"] }) {
  if (conn === "demo") return <Badge tone="warning">Demo</Badge>;
  if (conn === "no-session") return <Badge tone="danger">Signed out</Badge>;
  if (conn === "closed") return <Badge tone="danger">Offline</Badge>;
  if (conn === "connecting") return <Badge tone="muted">Connecting…</Badge>;
  if (phase === "live") return <Badge tone="success">Live</Badge>;
  if (phase === "wrapup") return <Badge tone="muted">Wrap-up</Badge>;
  return <Badge tone="primary">Ready</Badge>;
}

/** Idle: connected + a compact quick-reference (req 8.1). */
function IdleView({ conn }: { conn: ConnStatus }) {
  const top = rankValuePoints(VALUE_POINTS.filter((p) => p.products.includes("tint")), []).slice(0, 3);
  return (
    <div className="mx-auto max-w-[650px] space-y-3">
      <div className="rounded-lg border bg-card p-4 text-center">
        <p className="text-base font-semibold">
          {conn === "open" || conn === "demo" ? "Ready for the next call" : "Waiting for connection…"}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          The call card, pricing, and coaching light up the moment a call connects.
        </p>
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quick reference — top value points</p>
        {top.map((p) => (
          <div key={p.id} className="rounded-lg border bg-card p-2.5">
            <p className="text-sm font-semibold">{p.headline}</p>
            <p className="text-sm text-muted-foreground">“{p.line}”</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Incoming/ringing: caller ID display (req 8.2). */
function IncomingView({ counterpart }: { counterpart: string | null }) {
  return (
    <div className="mx-auto max-w-[650px]">
      <div className="rounded-lg border-2 border-primary/50 bg-card p-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Incoming call</p>
        <p className="mt-1 text-2xl font-bold">{counterpart ?? "Unknown caller"}</p>
      </div>
    </div>
  );
}
